// Mesmas convenções de `dashboard/ai/actions/aiActionApi.js`: base em
// `AI_SERVER_URL`, Bearer do cookie `userId`, chave de idempotência por
// escrita e um erro de cliente único `{ status, code, errorCode, message,
// retryable }` — nunca ecoa o corpo do servidor (nenhum `draft`/`reason`
// repassado). As cinco rotas consumidas aqui são as de Task 8 (BE); a lista
// `DISBURSEMENT_AI_ROUTES` existe só para o teste do BE e o deste arquivo
// comparem o mesmo array literal.
import Cookies from "universal-cookie";

const cookies = new Cookies();
const BASE = process.env.AI_SERVER_URL || "";
const LOOPBACK_HOSTS = new Set(["localhost", "127.0.0.1", "[::1]"]);

export const DISBURSEMENT_AI_ROUTES = Object.freeze([
  "POST /works/{work_id}/ai/disbursement/suggest",
  "POST /works/{work_id}/ai/disbursement/organize",
  "POST /works/{work_id}/ai/disbursement/organize/{draft_id}/confirm",
  "POST /works/{work_id}/ai/disbursement/rule-draft",
  "POST /works/{work_id}/ai/disbursement/rule-draft/{draft_id}/confirm",
]);

const clientError = ({ status, errorCode, message, retryable = false }) => ({
  status,
  code: errorCode,
  errorCode,
  message,
  retryable,
});

// Mensagens que o próprio corpo do erro não explica sozinho (o `errorCode` de
// negócio da regra/rascunho). Genéricas por status ficam em `safeMessage`.
const ERROR_MESSAGES = Object.freeze({
  ai_draft_version_conflict:
    "O rascunho mudou desde a revisão. Recarregue e confirme de novo.",
  ai_draft_not_found: "O rascunho expirou. Gere a proposta de novo.",
  ai_provider_unavailable:
    "O provedor de IA está temporariamente indisponível. Esta tentativa não consumiu sua cota.",
  ai_provider_invalid_output:
    "A IA não retornou uma proposta segura. Esta tentativa não consumiu sua cota.",
  presentation_read_only: "Modo somente leitura não permite alterações.",
});

const safeMessage = (status, errorCode) => {
  if (Object.hasOwn(ERROR_MESSAGES, errorCode))
    return ERROR_MESSAGES[errorCode];
  if (status === 401) return "Faça login novamente para usar a IA.";
  if (status === 403)
    return "As análises com IA não estão habilitadas nesta obra.";
  if (status === 404) return "A obra ou o rascunho não foi encontrado.";
  if (status === 409)
    return "Os dados desta proposta mudaram. Recarregue e tente novamente.";
  if (status === 410) return "O rascunho expirou. Gere a proposta de novo.";
  if (status === 422) return "Revise o pedido antes de tentar de novo.";
  if (status === 429)
    return "A cota de análises desta obra terminou neste período.";
  return "A IA está temporariamente indisponível.";
};

const retryableError = (status, errorCode) => {
  if (
    errorCode === "ai_provider_unavailable" ||
    errorCode === "ai_provider_invalid_output"
  ) {
    return true;
  }
  return status >= 500;
};

const configuredBase = () => {
  if (!BASE) {
    throw clientError({
      status: 0,
      errorCode: "configuration_error",
      message: "Análises com IA não configuradas.",
    });
  }
  try {
    const base = new URL(BASE);
    if (
      (base.protocol !== "https:" &&
        !(
          base.protocol === "http:" &&
          process.env.NODE_ENV === "development" &&
          LOOPBACK_HOSTS.has(base.hostname.toLowerCase())
        )) ||
      base.username ||
      base.password
    ) {
      throw new Error();
    }
    return base;
  } catch {
    throw clientError({
      status: 0,
      errorCode: "configuration_error",
      message: "Análises com IA não configuradas.",
    });
  }
};

const disbursementFetch = async (
  path,
  { body, idempotencyKey, signal } = {},
) => {
  const base = configuredBase();
  const basePath = base.pathname.replace(/\/+$/, "");
  const url = new URL(`${basePath}/${path}`, `${base.origin}/`);
  if (url.origin !== base.origin) {
    throw clientError({
      status: 0,
      errorCode: "configuration_error",
      message: "Análises com IA não configuradas.",
    });
  }
  const { token } = cookies.get("userId") || {};
  if (typeof token !== "string" || !token) {
    throw clientError({
      status: 401,
      errorCode: "authentication_required",
      message: safeMessage(401),
    });
  }
  const headers = {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };
  if (idempotencyKey) headers["Idempotency-Key"] = idempotencyKey;

  let response;
  try {
    response = await fetch(url.toString(), {
      method: "POST",
      headers,
      body: JSON.stringify(body ?? {}),
      ...(signal ? { signal } : {}),
    });
  } catch (error) {
    if (error?.name === "AbortError") {
      throw clientError({
        status: 0,
        errorCode: "request_cancelled",
        message: "Pedido cancelado.",
      });
    }
    throw clientError({
      status: 0,
      errorCode: "network_error",
      message: "Não foi possível comunicar com a IA.",
      retryable: true,
    });
  }

  const data = await response.json().catch(() => null);
  if (!response.ok) {
    const errorCode =
      typeof data?.errorCode === "string" ? data.errorCode : "request_failed";
    throw clientError({
      status: response.status,
      errorCode,
      message: safeMessage(response.status, errorCode),
      retryable: retryableError(response.status, errorCode),
    });
  }
  return data;
};

const workPath = (workId) =>
  `works/${encodeURIComponent(String(workId))}/ai/disbursement`;
const draftSegment = (draftId) => encodeURIComponent(String(draftId));

export const newIdempotencyKey = () => {
  const random =
    globalThis.crypto?.randomUUID?.() ??
    `${Date.now()}-${Math.random().toString(36).slice(2)}-${Math.random()
      .toString(36)
      .slice(2)}`;
  return `disb-${random}`.replace(/[^A-Za-z0-9._:-]/g, "-").slice(0, 128);
};

export const suggestDisbursementProfile = (
  workId,
  { targetKey },
  { signal } = {},
) =>
  disbursementFetch(`${workPath(workId)}/suggest`, {
    body: { targetKey },
    signal,
  });

export const organizeWithAi = (workId, { idempotencyKey, signal } = {}) =>
  disbursementFetch(`${workPath(workId)}/organize`, {
    body: {},
    idempotencyKey,
    signal,
  });

export const confirmOrganizeWithAi = (
  workId,
  draftId,
  { expectedVersion, acceptedFamilies },
  { idempotencyKey, signal } = {},
) =>
  disbursementFetch(
    `${workPath(workId)}/organize/${draftSegment(draftId)}/confirm`,
    {
      body: { expectedVersion, acceptedFamilies },
      idempotencyKey,
      signal,
    },
  );

export const describeRuleWithAi = (
  workId,
  { prompt },
  { idempotencyKey, signal } = {},
) =>
  disbursementFetch(`${workPath(workId)}/rule-draft`, {
    body: { prompt },
    idempotencyKey,
    signal,
  });

export const confirmRuleWithAi = (
  workId,
  draftId,
  { expectedVersion, targetKey, profile, description },
  { idempotencyKey, signal } = {},
) =>
  disbursementFetch(
    `${workPath(workId)}/rule-draft/${draftSegment(draftId)}/confirm`,
    {
      body: {
        expectedVersion,
        targetKey,
        profile,
        ...(description === undefined ? {} : { description }),
      },
      idempotencyKey,
      signal,
    },
  );

export default {
  newIdempotencyKey,
  suggestDisbursementProfile,
  organizeWithAi,
  confirmOrganizeWithAi,
  describeRuleWithAi,
  confirmRuleWithAi,
  DISBURSEMENT_AI_ROUTES,
};
